// frontend/super-admin/src/pages/Plans.jsx (NEW FILE)

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Tag,
  Space,
  message,
  Popconfirm,
  Divider,
  Row,
  Col,
  Select,
  Typography,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CrownOutlined,
  StarOutlined,
  EyeOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/pricing-plans');
      setPlans(response.data.data);
    } catch (error) {
      message.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      is_popular: false,
      billing_cycle: 'monthly',
      trial_days: 0,
      features: {
        whatsapp: true,
        instagram: false,
        catalog: true,
        chatbot: true,
        campaigns: true,
        analytics: false,
        api: false,
        team: false,
        priority_support: false,
        white_label: false,
        custom_domain: false
      }
    });
    setModalVisible(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    form.setFieldsValue({
      ...plan,
      features: plan.features || {}
    });
    setModalVisible(true);
  };

  const handleSave = async (values) => {
    try {
      const payload = {
        ...values,
        // Convert undefined limits to -1 (unlimited)
        contacts_limit: values.contacts_limit || -1,
        messages_limit: values.messages_limit || -1,
        team_members_limit: values.team_members_limit || -1,
        chatbot_flows_limit: values.chatbot_flows_limit || -1,
        campaigns_limit: values.campaigns_limit || -1,
        api_calls_limit: values.api_calls_limit || -1
      };

      if (editingPlan) {
        await api.put(`/api/admin/pricing-plans/${editingPlan.id}`, payload);
        message.success('Plan updated successfully!');
      } else {
        await api.post('/api/admin/pricing-plans', payload);
        message.success('Plan created successfully!');
      }

      setModalVisible(false);
      loadPlans();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save plan');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/pricing-plans/${id}`);
      message.success('Plan deleted successfully!');
      loadPlans();
    } catch (error) {
      message.error('Failed to delete plan');
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await api.patch(`/api/admin/pricing-plans/${plan.id}/toggle-active`);
      message.success(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`);
      loadPlans();
    } catch (error) {
      message.error('Failed to toggle status');
    }
  };

  const columns = [
    {
      title: 'Order',
      dataIndex: 'display_order',
      key: 'order',
      width: 80,
      sorter: (a, b) => a.display_order - b.display_order
    },
    {
      title: 'Plan Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {text}
          {record.is_popular && <StarOutlined style={{ color: '#faad14' }} />}
        </Space>
      )
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      render: (slug) => <Tag color="blue">{slug}</Tag>
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <div>
          <Text strong style={{ fontSize: 16 }}>₹{price}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            /{record.billing_cycle}
          </Text>
        </div>
      )
    },
    {
      title: 'Trial Days',
      dataIndex: 'trial_days',
      key: 'trial',
      render: (days) => (
        <Tag color={days > 0 ? 'green' : 'default'}>
          {days > 0 ? `${days} days` : 'No trial'}
        </Tag>
      )
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 11 }}>
            Contacts: {record.contacts_limit === -1 ? '∞' : record.contacts_limit}
          </Text>
          <Text style={{ fontSize: 11 }}>
            Messages: {record.messages_limit === -1 ? '∞' : record.messages_limit}
          </Text>
          <Text style={{ fontSize: 11 }}>
            Team: {record.team_members_limit === -1 ? '∞' : record.team_members_limit}
          </Text>
        </Space>
      )
    },
    {
      title: 'Active Subscriptions',
      key: 'subscriptions',
      render: (_, record) => (
        <Tag color="purple">{record.subscriptionCount || 0}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => handleToggleActive(record)}
          >
            {record.is_active ? 'Deactivate' : 'Activate'}
          </Button>
          <Popconfirm
            title="Delete this plan?"
            description="This will affect existing subscriptions!"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2}>💎 Pricing Plans Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Create New Plan
        </Button>
      </div>

      <Card>
        <Table
          dataSource={plans}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingPlan ? 'Edit Plan' : 'Create New Plan'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={900}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Plan Name"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input placeholder="Professional" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="slug"
                label="Slug (URL-friendly)"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[a-z0-9-]+$/, message: 'Only lowercase, numbers, hyphens' }
                ]}
              >
                <Input placeholder="professional" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={2} placeholder="Perfect for growing teams" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="price"
                label="Price (₹)"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  min={0}
                  step={100}
                  style={{ width: '100%' }}
                  placeholder="2999"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="billing_cycle"
                label="Billing Cycle"
                rules={[{ required: true }]}
              >
                <Select>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="yearly">Yearly</Select.Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="trial_days"
                label="Trial Days"
              >
                <InputNumber
                  min={0}
                  max={90}
                  style={{ width: '100%' }}
                  placeholder="7"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Resource Limits (-1 = Unlimited)</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="contacts_limit" label="Contacts">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="messages_limit" label="Messages/Month">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="team_members_limit" label="Team Members">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="chatbot_flows_limit" label="Chatbot Flows">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="campaigns_limit" label="Campaigns">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="api_calls_limit" label="API Calls/Month">
                <InputNumber
                  min={-1}
                  style={{ width: '100%' }}
                  placeholder="-1 (unlimited)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Features</Divider>

          <Row gutter={[16, 8]}>
            <Col span={8}>
              <Form.Item name={['features', 'whatsapp']} valuePropName="checked">
                <Checkbox>WhatsApp Business API</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'instagram']} valuePropName="checked">
                <Checkbox>Instagram Messaging</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'catalog']} valuePropName="checked">
                <Checkbox>Product Catalog</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'chatbot']} valuePropName="checked">
                <Checkbox>Chatbot Builder</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'campaigns']} valuePropName="checked">
                <Checkbox>Campaigns</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'analytics']} valuePropName="checked">
                <Checkbox>Advanced Analytics</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'api']} valuePropName="checked">
                <Checkbox>API Access</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'team']} valuePropName="checked">
                <Checkbox>Team Management</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'priority_support']} valuePropName="checked">
                <Checkbox>Priority Support</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'white_label']} valuePropName="checked">
                <Checkbox>White Label</Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['features', 'custom_domain']} valuePropName="checked">
                <Checkbox>Custom Domain</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="display_order" label="Display Order">
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="1"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="is_popular" valuePropName="checked">
                <Checkbox>Mark as Popular</Checkbox>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item name="is_active" valuePropName="checked">
                <Checkbox>Active (visible to users)</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" size="large">
                {editingPlan ? 'Update Plan' : 'Create Plan'}
              </Button>
              <Button onClick={() => setModalVisible(false)} size="large">
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}